import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, CalendarClock } from "lucide-react";
import { http, formatApiError, askDelete } from "../../lib/api";
import { DateInput } from "../DateInput";

const inp = "border border-slate-300 rounded-md px-3 py-2 text-sm bg-white";
const EMPTY = { time_from: "", time_to: "", name: "", company: "", host: "", purpose: "", plate: "" };

// Liste d'accès : visiteurs attendus sur le site pour une date
export function ExpectedVisitorsAdmin({ siteId, day, setDay }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState(EMPTY);
  const load = () => http.get("/expected-visitors", { params: { site_id: siteId, date: day } }).then((r) => setItems(r.data));
  useEffect(() => { if (siteId && day) load(); }, [siteId, day]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async (e) => {
    e.preventDefault();
    try { await http.post("/expected-visitors", { site_id: siteId, date: day, ...f }); setF(EMPTY); load(); toast.success("Visiteur annoncé"); }
    catch (er) { toast.error(formatApiError(er)); }
  };
  const del = async (x) => {
    if (!askDelete(`Retirer ${x.name} de la liste ?`)) return;
    try { await http.delete(`/expected-visitors/${x.id}`); load(); } catch (er) { toast.error(formatApiError(er)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="expected-visitors-admin">
      <div className="px-4 py-3 flex flex-wrap items-center gap-3 border-b border-slate-100">
        <CalendarClock className="h-4 w-4 text-amber-600" /><span className="font-bold">Visiteurs attendus le</span>
        <DateInput data-testid="expected-day" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="border border-slate-300 rounded-md px-2 py-1 text-sm" />
        <span className="text-xs text-slate-500" data-testid="expected-count">{items.length} annoncé(s)</span>
      </div>
      <form onSubmit={add} className="p-3 grid md:grid-cols-4 gap-2 bg-slate-50 border-b border-slate-100" data-testid="expected-form">
        <input data-testid="expected-name" required value={f.name} onChange={set("name")} placeholder="Nom et prénom *" className={inp} />
        <input data-testid="expected-company" value={f.company} onChange={set("company")} placeholder="Société" className={inp} />
        <input data-testid="expected-host" value={f.host} onChange={set("host")} placeholder="Personne visitée" className={inp} />
        <input data-testid="expected-purpose" value={f.purpose} onChange={set("purpose")} placeholder="Motif" className={inp} />
        <input data-testid="expected-plate" value={f.plate} onChange={set("plate")} placeholder="Véhicule (immat.)" className={`${inp} uppercase`} />
        <label className="text-xs text-slate-600 flex items-center gap-1">De<input data-testid="expected-from" type="time" value={f.time_from} onChange={set("time_from")} className={`${inp} flex-1`} /></label>
        <label className="text-xs text-slate-600 flex items-center gap-1">à<input data-testid="expected-to" type="time" value={f.time_to} onChange={set("time_to")} className={`${inp} flex-1`} /></label>
        <button data-testid="expected-add" className="bg-[#0B2A7A] text-white text-sm font-semibold rounded-md px-4 py-2">Annoncer</button>
      </form>
      <div className="divide-y divide-slate-100">
        {items.length === 0 && <div className="p-5 text-sm text-slate-500 text-center">Aucun visiteur annoncé ce jour.</div>}
        {items.map((x) => (
          <div key={x.id} className="px-4 py-2.5 flex items-center gap-3 text-sm" data-testid={`expected-row-${x.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold">{x.name}{x.company && <span className="font-normal text-slate-500"> · {x.company}</span>}</div>
              <div className="text-xs text-slate-500">{x.time_from || x.time_to ? `${x.time_from || "…"} – ${x.time_to || "…"}` : "Toute la journée"}{x.host && ` · ${x.host}`}{x.purpose && ` · ${x.purpose}`}{x.plate && ` · ${x.plate}`}{x.announced_by && ` · annoncé par ${x.announced_by}`}</div>
            </div>
            {x.arrived_at
              ? <span className="text-xs font-bold rounded px-2 py-0.5 bg-emerald-100 text-emerald-800" data-testid={`expected-arrived-${x.id}`}>Arrivé à {new Date(x.arrived_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
              : <button data-testid={`expected-delete-${x.id}`} onClick={() => del(x)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}
